<template>
    <div class="stats">
        <ul v-if="hasAllRoles(['DeviceView', 'DeviceEventView','NotificationsVideos']) && checkMediaAccessibilityLicense()">
            <li @click="openModal('videos')"></li>
            <div class="icon-wrapper">
                <span class="video-number">{{ videosCount }}</span>
                <icon icon="video-solid" :width="22" :height="20"></icon>
            </div>
        </ul>
        <ul v-if="hasAllRoles(['DeviceEventView'])">
            <li @click="openModal('events')"></li>
            <div class="icon-wrapper">
                <span class="event-number">{{ eventsCount }}</span>
                <icon icon="bell" :width="22" :height="20"></icon>
            </div>
        </ul>
        <resource-list v-model="eventsModal" ref="eventsList" title="Events" name="events" :handler="getEvents" :close-modals="closeModals" accept="false">
            <template v-slot:action>
                <div class="header">
                    {{ eventTitle }}
                    <icon icon="filter" class="filter-icon" @click.native="toggleFilter"></icon>
                </div>
                <sp-transition name="slide-x-left">
                    <resource-filter-layout v-if="getIsFilterActive" @applyFilters="handleApplyFilters" @resetFilters="handleResetFilters" title="Filter Events" type="Events">
                        <resource-event-filter :form="eventFiltersForm"></resource-event-filter>
                    </resource-filter-layout>
                </sp-transition>
            </template>
            <template v-slot:default="item">
                <event-item
                    :event="item.item"
                    :on-event-click="showEvent"
                ></event-item>
            </template>
        </resource-list>
        <resource-list v-model="videosModal" ref="mediaList" name="videos" :handler="getVideos" :close-modals="closeModals">
            <template v-slot:action>
                <div class="header">
                    {{ mediaTitle }}
                    <icon icon="filter" class="filter-icon" @click.native="toggleFilter"></icon>
                </div>
                <sp-transition name="slide-x-left">
                    <resource-filter-layout v-if="getIsFilterActive" @applyFilters="handleApplyFilters" @resetFilters="handleResetFilters" title="Filter Media Requests" type="Media">
                        <resource-media-filter :form="mediaFiltersForm"></resource-media-filter>
                    </resource-filter-layout>
                </sp-transition>
            </template>
            <template v-slot:default="item">
                <no-image-video-item
                    :video="item.item"
                    :on-event-click="showEvent"
                ></no-image-video-item>
            </template>
        </resource-list>
    </div>
</template>

<script>

import ResourceList from '@/components/layout/header/ResourceList'
import EventItem from '@/components/atoms/EventItem'
import NoImageVideoItem from '@/components/atoms/NoImageVideoItem'
import VideoItem from '@/components/atoms/VideoItem'
import Icon from '@/components/atoms/Icon'
import toast from '@/plugins/izitoast'
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { DeviceEventApi, StatisticsApi, MediaRequestApi } from '@/api'
import { CollectionTransformer, EventTransformer, MediaTransformer } from '@/transformers'
import { MEDIA_INTERVAL_TIME } from '@/config/mediaConfig'
import ResourceFilterLayout from '@/components/layout/header/ResourceFilterLayout'
import ResourceEventFilter from '@/components/layout/header/ResourceEventFilter'
import ResourceMediaFilter from '@/components/layout/header/ResourceMediaFilter'
import SpTransition from '@/components/transitions/SpTransition'

const deviceEventApi = new DeviceEventApi()
const statisticsApi = new StatisticsApi()
const mediaRequestApi = new MediaRequestApi()
const collectionTransformer = new CollectionTransformer()
const eventTransformer = new EventTransformer()
const mediaTransformer = new MediaTransformer()

export default {
    name: 'ResourceStats',
    components: { ResourceList, EventItem, VideoItem, Icon, ResourceFilterLayout, ResourceEventFilter, ResourceMediaFilter, SpTransition, NoImageVideoItem },
    data () {
        return {
            eventsCount: 0,
            snapshots: 0,
            videosCount: 0,
            eventsModal: false,
            snapshotsModal: false,
            videosModal: false,
            interval: null,
            mediaTitle: 'Media',
            eventTitle: 'Events',
            eventFiltersForm: {
                VRN: '',
                dateFrom: moment().subtract(10, 'd').startOf('d')._d,
                dateTo: moment().subtract(0, 'd').endOf('d')._d,
                // fleets: null,
                drivers: null,
                // eventCategories: null,
                // eventTypes: null,
                eventClassifications: null
            },
            mediaFiltersForm: {
                VRN: '',
                dateFrom: moment().subtract(10, 'd').startOf('d')._d,
                dateTo: moment().subtract(0, 'd').endOf('d')._d,
                fleets: []
            }
        }
    },
    props: {
        driverInfo: {
            type: Boolean,
            default: true
        }
    },
    mounted () {
        if (this.hasAllRoles(['DeviceView'])) {
            this.getSummary()

            this.interval = setInterval(() => {
                this.getSummary()
            }, MEDIA_INTERVAL_TIME)
        }
    },
    beforeDestroy () {
        clearInterval(this.interval)
    },
    computed: {
        ...mapGetters({
            getEventTypes: 'dictionaryModule/getEventTypes',
            getDeviceCommandStates: 'dictionaryModule/getDeviceCommandStates',
            getAccessToken: 'authModule/getAccessToken',
            getSpeedUnit: 'authModule/getSpeedUnit',
            getIsFilterActive: 'filterModule/getIsFilterActive'
        })
    },
    methods: {
        closeModals (name) {
            switch (name) {
                case 'events':
                    this.eventsModal = false
                    break
                case 'snapshots':
                    this.snapshotsModal = false
                    break
                case 'videos':
                    this.videosModal = false
                    break
                default:
                    this.eventsModal = false
                    this.snapshotsModal = false
                    this.videosModal = false
            }
        },
        openModal (value) {
            this.clearForm('Media')
            this.clearForm('Events')
            switch (value) {
                case 'events':
                    this.snapshotsModal = false
                    this.videosModal = false
                    this.eventsModal = !this.eventsModal
                    break
                case 'snapshots':
                    this.eventsModal = false
                    this.videosModal = false
                    this.snapshotsModal = !this.snapshotsModal
                    break
                case 'videos':
                    this.eventsModal = false
                    this.snapshotsModal = false
                    this.videosModal = !this.videosModal
            }
        },
        showSnapshot (snapshotId) {
            this.$store.commit('setSnapshotId', snapshotId)
            this.closeModals()
        },
        showEvent (payload) {
            this.$store.commit('setEventId', payload)
            this.closeModals()
        },
        async getSummary () {
            if (null == this.getAccessToken) {
                return
            }

            const mediaResponse = await statisticsApi.getDeviceStatistics()

            if (!mediaResponse.isSuccess) {
                toast.error('Couldn\'t get medias and events')
                return
            }

            this.videosCount = mediaResponse.payload.totalMediaToday
            this.eventsCount = mediaResponse.payload.totalEventsToday
        },
        clearForm (type) {
            switch (type) {
                case 'Media':
                    this.mediaFiltersForm = {
                        VRN: '',
                        dateFrom: moment().subtract(10, 'd').startOf('d').format('YYYY-MM-DD'),
                        dateTo: moment().subtract(0, 'd').endOf('d').format('YYYY-MM-DD'),
                        fleets: []
                    }
                    break
                case 'Events':
                    this.eventFiltersForm = {
                        VRN: '',
                        dateFrom: moment().subtract(10, 'd').startOf('d').format('YYYY-MM-DD'),
                        dateTo: moment().subtract(0, 'd').endOf('d').format('YYYY-MM-DD'),
                        drivers: null,
                        eventClassifications: null
                    }
                    break
            }
        },
        handleApplyFilters (type) {
            switch (type) {
                case 'Media':
                    this.$refs.mediaList.applyFilters()
                    break
                case 'Events':
                    this.$refs.eventsList.applyFilters()
                    break
            }
        },
        handleResetFilters (type) {
            switch (type) {
                case 'Media':
                    this.clearForm(type)
                    this.$refs.mediaList.applyFilters()
                    break
                case 'Events':
                    this.clearForm(type)
                    this.$refs.eventsList.applyFilters()
                    break
            }
        },

        async getEvents (page, perPage) {
            const availableEventCategories = [1, 2, 3, 5]
            const response = await deviceEventApi.getDeviceEvents({
                dateFrom: moment(this.eventFiltersForm.dateFrom).startOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z',
                dateTo: moment(this.eventFiltersForm.dateTo).endOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z',
                eventTypes: this.eventFiltersForm.eventTypes,
                eventClassifications: this.eventFiltersForm.eventClassifications,
                vehicleRegistrationNumber: this.eventFiltersForm.VRN,
                page: page,
                eventCategories: availableEventCategories,
                recordLimit: perPage
            })

            const events = []
            if (0 < response.payload.items.length) {
                events.push(...collectionTransformer.transform(eventTransformer, response.payload.items))
                this.page++
            }
            return events
        },
        async getSnapshot (page, perPage) {
            return []
        },
        async getVideos (page, perPage) {
            const response = await mediaRequestApi.getMediaRequests({
                videoTimeFrom: moment(this.mediaFiltersForm.dateFrom).startOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z',
                videoTimeTo: moment(this.mediaFiltersForm.dateTo).endOf('d').format('YYYY-MM-DDTHH:mm:ss').toString() + 'Z',
                registrationNumber: this.mediaFiltersForm.VRN,
                fleetIds: this.mediaFiltersForm.fleets.map(fleet => fleet.id),
                page: page * 200,
                take: 200
            })

            if (!response.isSuccess) {
                return []
            }

            const media = []
            if (0 < response.payload.items.length) {
                media.push(...collectionTransformer.transform(mediaTransformer, response.payload.items))
                this.page++
            }

            return media
        },

        toggleFilter () {
            this.setIsFilterActive(!this.getIsFilterActive)
        },

        ...mapActions('filterModule', {
            setIsFilterActive: 'setIsFilterActive'
        })
    }
}
</script>

<style scoped lang="scss">
.stats {
    display: flex;
    align-items: center;
    line-height: 1rem;

    ul {
        display: flex;
        padding: 0;
        margin: 0 0 0 20px;
        position: relative;
        transition: opacity 0.3s;

        @media (min-width: 962px) {
            margin: 0 0 0 30px;
        }

        &:hover {
            opacity: 0.7;
        }

        li {
            display: flex;
            color: $primary;
            cursor: pointer;
            position: absolute;
            padding: 25px;
            top: -26px;
            right: -17px;
            z-index: 99999;

            .number {
                color: $font;
            }
        }
    }
}

.icon-wrapper {
    position: relative;
    color: $icon-primary;
}

.event-number {
    position: absolute;
    top: -19px;
    left: 15px;
    color: #fff;
    border-radius: 50%;
    padding: 4px 6px;
    background-color: $primary-default;
    font-size: 12px;
    font-weight: 700;
}

.video-number {
    position: absolute;
    top: -19px;
    left: 15px;
    color: #fff;
    border-radius: 50%;
    padding: 4px 6px;
    background-color: $primary-default;
    font-size: 12px;
    font-weight: 700;
}

.header {
    font-size: 16px;
    font-weight: bold;
    border-top: 3px solid $primary-default;
    border-bottom: 2px solid lightgray;
    color: $font;
    background: $secondary;
    padding: 15px 10px;
    display: flex;
    justify-content: space-between;
}

</style>
